import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { TabsContent } from '../ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Eye, Edit, Trash2, Plus, Users } from 'lucide-react';
import { ListPager, usePagedList } from './ListPager';
import { ListToolbar, useFilteredList } from './ListToolbar';
import { LANG_LABELS_JA, LangCounts } from './GuidesTab';
import { getCourseColorOption } from '../../lib/courseColors';

const SchedulesTab = ({
  sortedSchedules, handleScheduleSort, scheduleSortConfig, SortIndicatorFor,
  openDialog, deleteSchedule, formatDateShort, t,
  courses = [], language = 'ja', applications = [],
}) => {
  const [applicantsDialog, setApplicantsDialog] = useState(false);
  const [applicantsList, setApplicantsList] = useState([]);
  const [applicantsSchedule, setApplicantsSchedule] = useState(null);
  const [courseFilter, setCourseFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);

  const selectedFilterCourse = courses.find(c => c.id === courseFilter);
  const displaySchedules = courseFilter === 'all'
    ? sortedSchedules
    : sortedSchedules.filter(s => s.courseId === courseFilter || (selectedFilterCourse && s.courseName === selectedFilterCourse.name?.ja));

  const today = new Date().toISOString().slice(0, 10);
  const isPast = (s) => ((s.endDate || s.startDate || '') < today);
  const upcomingSchedules = displaySchedules.filter(s => !isPast(s));
  const pastSchedules = displaySchedules.filter(isPast);

  const getLocalName = (schedule) => {
    const cr = courses.find(c => c.id === schedule.courseId) || courses.find(c => c.name?.ja === schedule.courseName);
    return cr?.name?.[language] || cr?.name?.ja || schedule.courseName;
  };

  const upcomingFilter = useFilteredList(
    upcomingSchedules,
    ['courseName', 'location', 'notes', s => getLocalName(s)],
    [{ key: 'status', label: t('admin.common.status'), options: [
      { value: 'open', label: t('admin.schedules.open') },
      { value: 'in_progress', label: t('admin.schedules.inProgress') },
      { value: 'full', label: t('admin.schedules.full') },
      { value: 'closed', label: t('admin.schedules.closed') },
    ] }]
  );
  const pastFilter = useFilteredList(pastSchedules, ['courseName', 'location', 'notes', s => getLocalName(s)]);
  const pager = usePagedList(upcomingFilter.result);
  const pastPager = usePagedList(pastFilter.result);

  const scheduleApps = (schedule) => applications.filter(a => {
    if (a.courseId !== schedule.courseId) return false;
    const pd = a.preferredDate || '';
    return pd >= schedule.startDate && pd <= (schedule.endDate || schedule.startDate);
  });
  // 受講者ごとの言語（受講者未登録なら申込の言語）。キャンセルは除外
  const participantLangs = (apps) => apps.filter(a => a.status !== 'cancelled').flatMap(a => {
    const named = (a.participants || []).filter(p => p?.name);
    return named.length ? named.map(p => p.language || a.language || '') : [a.language || ''];
  });
  const langSummary = (schedule) => {
    const counts = {};
    participantLangs(scheduleApps(schedule)).forEach(c => { const k = c || 'none'; counts[k] = (counts[k] || 0) + 1; });
    return [...Object.keys(LANG_LABELS_JA), 'none', ...Object.keys(counts).filter(k => k !== 'none' && !LANG_LABELS_JA[k])]
      .filter(k => counts[k]).map(k => `${k === 'none' ? '未設定' : (LANG_LABELS_JA[k] || k)}${counts[k]}`);
  };

  const showApplicants = (schedule) => {
    const matched = scheduleApps(schedule);
    setApplicantsList(matched);
    setApplicantsSchedule(schedule);
    setApplicantsDialog(true);
  };

  const renderRow = (schedule) => {
    const colorOpt = getCourseColorOption(
      (courses.find(c => c.id === schedule.courseId) || courses.find(c => c.name?.ja === schedule.courseName))?.color
    );
    return (
    <TableRow key={schedule.id} className={isPast(schedule) ? 'bg-gray-50/60' : ''}>
      <TableCell className="font-medium">
        <span className="flex items-center gap-2">
          {colorOpt && <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${colorOpt.bg}`} />}
          <span className={colorOpt ? colorOpt.text : ''}>{getLocalName(schedule)}</span>
        </span>
      </TableCell>
      <TableCell>{formatDateShort(schedule.startDate)}</TableCell>
      <TableCell>{formatDateShort(schedule.endDate)}</TableCell>
      <TableCell>{schedule.capacity}{t('admin.schedules.people')}</TableCell>
      <TableCell>
        <button
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium cursor-pointer transition-colors ${
            schedule.enrolled > 0 ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'text-gray-500'
          }`}
          onClick={() => showApplicants(schedule)}
          data-testid={`show-applicants-${schedule.id}`}
        >
          <Users className="h-3.5 w-3.5" />
          {schedule.enrolled}{t('admin.schedules.people')}
        </button>
        {langSummary(schedule).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-1.5 text-[11px] text-gray-500 leading-tight" data-testid={`schedule-lang-summary-${schedule.id}`}>
            {langSummary(schedule).map(item => <span key={item} className="whitespace-nowrap">{item}</span>)}
          </div>
        )}
      </TableCell>
      <TableCell>{schedule.location || '-'}</TableCell>
      <TableCell>
        <Badge data-testid={`schedule-status-${schedule.id}`} className={schedule.status === 'open' ? 'bg-green-100 text-green-800' : schedule.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : schedule.status === 'full' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}>
          {schedule.status === 'open' ? t('admin.schedules.open') : schedule.status === 'in_progress' ? t('admin.schedules.inProgress') : schedule.status === 'full' ? t('admin.schedules.full') : t('admin.schedules.closed')}
        </Badge>
      </TableCell>
      <TableCell className="text-xs text-gray-500 max-w-[200px] truncate" title={schedule.notes || ''}>{schedule.notes || '-'}</TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => openDialog('editSchedule', schedule)}><Edit className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteSchedule(schedule.id)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </TableCell>
    </TableRow>
    );
  };

  return (
  <TabsContent value="schedules" className="p-4">
    <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <Button onClick={() => openDialog('newSchedule')} className="bg-[#00A651] hover:bg-[#008a43]">
        <Plus className="h-4 w-4 mr-2" /> {t('admin.schedules.title')}
      </Button>
      <div className="flex items-center gap-2 sm:ml-auto">
        <span className="text-sm text-gray-500 whitespace-nowrap">講習で絞り込み</span>
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-full sm:w-64" data-testid="schedule-course-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべての講習</SelectItem>
            {courses.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name?.[language] || c.name?.ja}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
    <ListToolbar filter={upcomingFilter} testId="schedules" placeholder="講習名・会場・備考で検索" />
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleScheduleSort('courseName')}>
              <div className="flex items-center gap-1">{t('admin.certificates.courseName')}<SortIndicatorFor config={scheduleSortConfig} columnKey="courseName" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleScheduleSort('startDate')}>
              <div className="flex items-center gap-1">{t('admin.schedules.startDate')}<SortIndicatorFor config={scheduleSortConfig} columnKey="startDate" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleScheduleSort('endDate')}>
              <div className="flex items-center gap-1">{t('admin.schedules.endDate')}<SortIndicatorFor config={scheduleSortConfig} columnKey="endDate" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleScheduleSort('capacity')}>
              <div className="flex items-center gap-1">{t('admin.schedules.capacity')}<SortIndicatorFor config={scheduleSortConfig} columnKey="capacity" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleScheduleSort('enrolled')}>
              <div className="flex items-center gap-1">{t('admin.stats.applications')}<SortIndicatorFor config={scheduleSortConfig} columnKey="enrolled" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleScheduleSort('location')}>
              <div className="flex items-center gap-1">{t('admin.schedules.location')}<SortIndicatorFor config={scheduleSortConfig} columnKey="location" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleScheduleSort('status')}>
              <div className="flex items-center gap-1">{t('admin.common.status')}<SortIndicatorFor config={scheduleSortConfig} columnKey="status" /></div>
            </TableHead>
            <TableHead>{t('admin.form.notes')}</TableHead>
            <TableHead className="w-[100px]">{t('admin.common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {upcomingFilter.result.length === 0 && pastFilter.result.length === 0 ? (
            <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">{t('admin.schedules.title')}</TableCell></TableRow>
          ) : (
            <>
              {pager.paged.map(renderRow)}
              {pastFilter.result.length > 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={9} className="text-center py-3 bg-gray-50/50">
                    <button
                      className="inline-flex items-center gap-1 text-sm font-medium text-[#00A651] hover:underline"
                      onClick={() => setShowPast(v => !v)}
                      data-testid="toggle-past-schedules"
                    >
                      {showPast
                        ? t('admin.schedules.hideCompleted')
                        : `${t('admin.schedules.showCompleted')}（${pastFilter.result.length}）`}
                    </button>
                  </TableCell>
                </TableRow>
              )}
              {showPast && pastPager.paged.map(renderRow)}
            </>
          )}
        </TableBody>
      </Table>
    </div>
    <ListPager pager={pager} testId="schedules" />
    {showPast && <ListPager pager={pastPager} testId="past-schedules" />}

    {/* Applicants Dialog */}
    <Dialog open={applicantsDialog} onOpenChange={setApplicantsDialog}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            {t('admin.stats.applications')} ({applicantsList.length}{t('admin.schedules.people')})
          </DialogTitle>
          <DialogDescription>
            {applicantsSchedule && `${getLocalName(applicantsSchedule)} (${applicantsSchedule.startDate} 〜 ${applicantsSchedule.endDate || applicantsSchedule.startDate})`}
          </DialogDescription>
        </DialogHeader>
        {applicantsList.length === 0 ? (
          <p className="text-center text-gray-500 py-6">{t('admin.applications.noApplications')}</p>
        ) : (
          <>
          <LangCounts codes={participantLangs(applicantsList)} label="受講者の言語別人数（キャンセル除く）" testId="schedule-applicants" />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.common.name')}</TableHead>
                <TableHead>{t('admin.common.email')}</TableHead>
                <TableHead>{t('admin.form.language')}</TableHead>
                <TableHead>{t('admin.common.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {applicantsList.map((app) => (
                <TableRow key={app.id}>
                  <TableCell className="font-medium">{app.name}</TableCell>
                  <TableCell className="text-sm text-gray-600">{app.email || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {participantLangs([{ ...app, status: '' }]).map((c, i) => (
                        <Badge key={i} className="bg-indigo-50 text-indigo-700 border-indigo-200 whitespace-nowrap">{LANG_LABELS_JA[c] || c || '-'}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      app.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                      app.status === 'completed_passed' ? 'bg-green-100 text-green-700' :
                      app.status === 'completed_failed' ? 'bg-red-100 text-red-700' :
                      app.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                      'bg-gray-100 text-gray-700'
                    }>
                      {app.status === 'pending' ? t('admin.applications.pending') :
                       app.status === 'confirmed' ? t('admin.applications.confirmed') :
                       app.status === 'completed_passed' ? t('admin.certificates.passed') :
                       app.status === 'completed_failed' ? t('admin.certificates.failed') :
                       app.status === 'cancelled' ? t('admin.common.cancelled') :
                       app.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  </TabsContent>
  );
};

export default SchedulesTab;
